import classNames from 'classnames';
import PreFilledByAITooltip from 'components/atoms/PreFilledByAITooltip.component';
import { useHighlight } from 'hooks/useHighlight';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import ReactSelect, { components as ReactSelectComponents } from 'react-select';
import type { StylesConfig } from 'react-select';
import { getTracingEvents } from 'utils/amplitude';
import { captureLeftRightArrowEvents } from 'utils/captureLeftRightArrowEvents';
import { ChevronDownIconOutline, XIconOutline } from 'utils/icons';
import withAsyncOptions from './bases/withAsyncOptions';
import withAsyncOptionsPaginated from './bases/withAsyncOptionsPaginated';
import withDefault from './bases/withDefault';
import withAsyncSearch from './extensions/withAsyncSearch';
import withControlledInputValue from './extensions/withControlledInputValue';
import withCreate from './extensions/withCreate';
import withOptionsDict from './extensions/withOptionsDict';
import withRenderedText from './extensions/withRenderedText';
import { getSelectClassNames } from './helpers';
import type { SelectConfig, SelectProps } from './select.types';

const getBase = (mode: SelectProps['mode']) => {
  if (mode === 'asyncOptions') return withAsyncOptions(ReactSelect);
  if (mode === 'asyncOptionsPaginated')
    return withAsyncOptionsPaginated(ReactSelect);
  return withDefault(ReactSelect);
};

const addExtension = (
  Base: FC<SelectConfig>,
  creatable: SelectProps['creatable'],
  asyncSearch: SelectProps['asyncSearch'],
) => {
  let maybeWrappedBase = withRenderedText(Base);
  // withCreate must be first since it can manipulate onInputValue params
  if (creatable) maybeWrappedBase = withCreate(maybeWrappedBase);
  if (asyncSearch) maybeWrappedBase = withAsyncSearch(maybeWrappedBase);
  return withControlledInputValue(withOptionsDict(maybeWrappedBase));
};

const DropdownIndicator: typeof ReactSelectComponents.DropdownIndicator = (
  props,
) => (
  <ReactSelectComponents.DropdownIndicator {...props}>
    <ChevronDownIconOutline
      className={classNames(
        'h-[1.2rem] w-[1.2rem]',
        props.isDisabled ? 'text-primary-200' : 'text-primary',
      )}
    />
  </ReactSelectComponents.DropdownIndicator>
);

const ClearIndicator: typeof ReactSelectComponents.ClearIndicator = (props) => (
  <ReactSelectComponents.ClearIndicator
    {...props}
    className={classNames(props.className, 'cursor-pointer')}
  >
    <XIconOutline className="h-[1.2rem] w-[1.2rem]" />
  </ReactSelectComponents.ClearIndicator>
);

// Must sit above the app modal overlay (Modal portal uses z-[100]) so menus
// rendered into document.body via menuPortalTarget remain visible.
const MODAL_PORTAL_Z_INDEX = 9999;

const Select = (props: SelectProps) => {
  const {
    mode = 'normal',
    creatable,
    asyncSearch,
    components,
    styles,
    themeOptions,
    isSearchable,
    filterOption,
    blurInputOnSelect,
    tracing,
    onMenuOpen,
    onChange,
    onFocus,
    onBlur,
    error,
    highlightID,
    size = 'base',
    // onMenuScrollToEnd,
    // onMenuScrollToBottom,
    aiTooltip,
    wrapperClassName,
    ...rest
  } = props;
  const Base = useMemo(
    () => addExtension(getBase(mode), creatable, asyncSearch),
    [mode, creatable, asyncSearch],
  );

  const highlightAPI = useHighlight();

  const highlightClassName = highlightAPI.shouldHighlight(highlightID)
    ? highlightAPI.className
    : undefined;

  const [isFocused, setIsFocused] = useState(false);

  const mergedStyles = useMemo<StylesConfig | undefined>(() => {
    if (!rest.menuPortalTarget) return styles;
    const userMenuPortal = styles?.menuPortal;
    return {
      ...styles,
      menuPortal: (base, state) => ({
        ...(userMenuPortal ? userMenuPortal(base, state) : base),
        zIndex: MODAL_PORTAL_Z_INDEX,
      }),
    };
  }, [rest.menuPortalTarget, styles]);

  const tracingEvents = getTracingEvents(tracing, {
    onMenuOpen,
    onChange,
    onFocus: (e: any) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    onBlur: (e: any) => {
      setIsFocused(false);
      onBlur?.(e);
    },
  });

  return (
    <PreFilledByAITooltip
      enabled={!rest.isDisabled && aiTooltip?.enabled}
      hide={isFocused}
      description={aiTooltip?.description}
    >
      <div className={classNames(wrapperClassName)}>
        <Base
          data-highlight-id={highlightID}
          {...rest}
          styles={mergedStyles}
          unstyled
          classNames={{
            ...getSelectClassNames({
              size,
              isError: !!error,
              useAIColorScheme: aiTooltip?.enabled,
              menuClassName: themeOptions?.menuClassName,
              menuListClassName: themeOptions?.menuListClassName,
              optionClassName: themeOptions?.optionClassName,
              controlClassName: themeOptions?.controlClassName,
              inputClassName: themeOptions?.inputClassName,
              valueContainerClassName: themeOptions?.valueContainerClassName,
              containerClassName: classNames(
                themeOptions?.containerClassName,
                highlightClassName,
              ),
            }),
            ...rest.classNames,
          }}
          components={{
            ClearIndicator: themeOptions?.showClearButton
              ? ClearIndicator
              : null,
            DropdownIndicator: themeOptions?.hideDropdownIndicator
              ? null
              : DropdownIndicator,
            ...components,
          }}
          loadingMessage={() => null}
          className={classNames('min-w-0', rest.className)}
          isCreatable={creatable} // used as hint for on of the extenstions that comes before the withcreate extention
          isSearchable={!!isSearchable}
          filterOption={isSearchable ? filterOption : null}
          blurInputOnSelect={blurInputOnSelect ?? true}
          onKeyDown={captureLeftRightArrowEvents}
          tabSelectsValue={false}
          {...tracingEvents}
        />
      </div>
    </PreFilledByAITooltip>
  );
};

export default Select;
export { ReactSelectComponents as components };
